import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { FormEvent, ReactElement, useEffect, useMemo, useState } from "react";
import { AppColors } from "../../constants/AppColors";
import { PageHeader } from "../../components/cards/PageHeader";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  createGuest,
  deleteGuest,
  loadGuests,
} from "../../store/slices/guestSlice";
import { useTranslation } from "../../translations/i18n";
import { formatMoney } from "../../utils/helperFunctions/currencyFormatter";

const emptyForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  country: "",
  document_number: "",
  notes: "",
  marketing_consent: false,
};

const GuestsScreen = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { guests, loading, saving, error } = useAppSelector(
    (state) => state.guests,
  );
  const { account } = useAppSelector((state) => state.auth);

  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    dispatch(loadGuests(undefined));
  }, [dispatch]);

  const currency = account?.default_currency || "BAM";

  const stats = useMemo(() => {
    const totalGuests = guests.length;
    const returningGuests = guests.filter(
      (guest) => Number(guest.bookings_count || 0) > 1,
    ).length;
    const marketingConsent = guests.filter(
      (guest) => guest.marketing_consent,
    ).length;
    const totalRevenue = guests.reduce(
      (sum, guest) => sum + Number(guest.total_revenue || 0),
      0,
    );

    return {
      totalGuests,
      returningGuests,
      marketingConsent,
      totalRevenue,
    };
  }, [guests]);

  const filteredGuests = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return guests;
    }

    return guests.filter((guest) => {
      return [
        guest.full_name,
        guest.email,
        guest.phone,
        guest.country,
        guest.notes,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        );
    });
  }, [guests, search]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!form.first_name.trim()) {
      return;
    }

    const result = await dispatch(
      createGuest({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        country: form.country.trim() || null,
        document_number: form.document_number.trim() || null,
        notes: form.notes.trim() || null,
        marketing_consent: form.marketing_consent,
      }),
    );

    if (createGuest.fulfilled.match(result)) {
      setForm(emptyForm);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t("guests.deleteConfirm"))) {
      return;
    }

    await dispatch(deleteGuest(id));
  };

  return (
    <Box>
      <PageHeader
        title={t("guests.title")}
        description={t("guests.subtitle")}
        action={
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              const element = document.getElementById("new-guest-form");
              element?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            {t("guests.newGuest")}
          </Button>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <StatCard label={t("guests.totalGuests")} value={stats.totalGuests} />
        <StatCard
          label={t("guests.returningGuests")}
          value={stats.returningGuests}
        />
        <StatCard
          label={t("guests.marketingConsent")}
          value={stats.marketingConsent}
        />
        <StatCard
          label={t("guests.totalRevenue")}
          value={formatMoney(stats.totalRevenue, currency)}
        />
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 340px" },
          gap: 3,
          alignItems: "flex-start",
        }}
      >
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${AppColors.border}`,
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{
                mb: 2,
                justifyContent: "space-between",
                alignItems: { xs: "stretch", md: "center" },
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: AppColors.primary,
                    fontWeight: 900,
                    textTransform: "uppercase",
                    fontSize: 12,
                  }}
                >
                  {t("guests.directory")}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {t("guests.contacts")}
                </Typography>
              </Box>

              <TextField
                size="small"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("guests.searchPlaceholder")}
                sx={{ minWidth: { xs: "100%", md: 320 } }}
              />
            </Stack>

            {loading ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <CircularProgress />
              </Box>
            ) : null}

            {!loading && filteredGuests.length === 0 ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography sx={{ fontWeight: 900 }}>
                  {t("guests.emptyTitle")}
                </Typography>
                <Typography color="text.secondary">
                  {t("guests.emptySubtitle")}
                </Typography>
              </Box>
            ) : null}

            <Stack spacing={1.5}>
              {filteredGuests.map((guest) => (
                <Card
                  key={guest.id}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${AppColors.border}`,
                    bgcolor: AppColors.surface,
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={2}
                      sx={{ justifyContent: "space-between" }}
                    >
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ minWidth: 0, alignItems: "flex-start" }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "14px",
                            bgcolor: AppColors.primarySoft,
                            color: AppColors.primary,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 900,
                            flexShrink: 0,
                          }}
                        >
                          {getInitials(guest.full_name)}
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 900 }}>
                            {guest.full_name}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={1.5}
                            sx={{ mt: 0.5, flexWrap: "wrap" }}
                          >
                            {guest.email ? (
                              <TinyInfo text={guest.email} />
                            ) : null}
                            {guest.phone ? (
                              <TinyInfo text={guest.phone} />
                            ) : null}

                            {guest.country ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {guest.country}
                              </Typography>
                            ) : null}
                          </Stack>

                          {guest.notes ? (
                            <Box
                              sx={{
                                mt: 1,
                                px: 1.5,
                                py: 1,
                                borderRadius: 2,
                                bgcolor: AppColors.background,
                                border: `1px solid ${AppColors.border}`,
                              }}
                            >
                              <Typography variant="body2">
                                {guest.notes}
                              </Typography>
                            </Box>
                          ) : null}
                        </Box>
                      </Stack>

                      <Stack
                        spacing={1}
                        sx={{
                          alignItems: { xs: "flex-start", md: "flex-end" },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}
                        >
                          <Chip
                            size="small"
                            label={
                              guest.marketing_consent
                                ? t("guests.hasConsent")
                                : t("guests.noConsent")
                            }
                            color={
                              guest.marketing_consent ? "success" : "default"
                            }
                          />

                          <Chip
                            size="small"
                            label={`${guest.bookings_count || 0} ${t(
                              "guests.stays",
                            )}`}
                            color="primary"
                            variant="outlined"
                          />
                        </Stack>

                        <Typography sx={{ fontWeight: 900 }}>
                          {formatMoney(
                            Number(guest.total_revenue || 0),
                            currency,
                          )}
                        </Typography>

                        {guest.last_stay_at ? (
                          <Typography variant="body2" color="text.secondary">
                            {t("guests.lastStay")}: {guest.last_stay_at}
                          </Typography>
                        ) : null}

                        <IconButton
                          size="small"
                          onClick={() => handleDelete(guest.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card
          id="new-guest-form"
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${AppColors.border}`,
            position: { lg: "sticky" },
            top: { lg: 24 },
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              sx={{
                color: AppColors.primary,
                fontWeight: 900,
                textTransform: "uppercase",
                fontSize: 12,
              }}
            >
              {t("guests.newGuest")}
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
              {t("guests.contactDetails")}
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label={t("guests.firstName")}
                  value={form.first_name}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      first_name: event.target.value,
                    }))
                  }
                  required
                />

                <TextField
                  size="small"
                  label={t("guests.lastName")}
                  value={form.last_name}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      last_name: event.target.value,
                    }))
                  }
                />

                <TextField
                  size="small"
                  label={t("guests.email")}
                  type="email"
                  value={form.email}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      email: event.target.value,
                    }))
                  }
                />

                <TextField
                  size="small"
                  label={t("guests.phone")}
                  value={form.phone}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      phone: event.target.value,
                    }))
                  }
                />

                <TextField
                  size="small"
                  label={t("guests.country")}
                  value={form.country}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      country: event.target.value,
                    }))
                  }
                />

                <TextField
                  size="small"
                  label={t("guests.documentNumber")}
                  value={form.document_number}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      document_number: event.target.value,
                    }))
                  }
                />

                <TextField
                  size="small"
                  label={t("guests.notes")}
                  value={form.notes}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      notes: event.target.value,
                    }))
                  }
                  multiline
                  minRows={2}
                />

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <Checkbox
                    checked={form.marketing_consent}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        marketing_consent: event.target.checked,
                      }))
                    }
                  />
                  <Typography variant="body2">
                    {t("guests.marketingConsent")}
                  </Typography>
                </Stack>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  sx={{ borderRadius: 999, fontWeight: 900 }}
                >
                  {t("guests.addGuest")}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

const getInitials = (name: string) => {
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const StatCard = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${AppColors.border}`,
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography color="text.secondary" sx={{ fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 950, mt: 1 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

const TinyInfo = ({ icon, text }: { icon?: ReactElement; text: string }) => {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      {icon || null}
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Stack>
  );
};

export default GuestsScreen;
